import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import discountApi, { Discount, UpdateDiscountData } from "../../api/discount";
import Sidebar from "../../components/common/Sidebar";
import Header from "../../components/common/Header";
const EditDiscount: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [discount, setDiscount] = useState<any | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (id) {
      fetchDiscount();
    }
  }, [id]);

  const fetchDiscount = async () => {
    try {
      setLoading(true);
      const response = await discountApi.getDiscount(id!);
      setDiscount(response.discount);
    } catch (error) {
      console.error("Error fetching discount:", error);
      navigate("/discounts");
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    if (!discount) return false;

    const newErrors: Record<string, string> = {};

    // Required fields
    if (!discount.code.trim()) {
      newErrors.code = "Discount code is required";
    }

    if (discount.value <= 0) {
      newErrors.value = "Value must be greater than 0";
    }

    if (!discount.attachedTo.name.trim()) {
      newErrors.attachedToName = "Bonus name is required";
    }

    if (discount.attachedTo.value <= 0) {
      newErrors.attachedToValue = "Bonus value must be greater than 0";
    }

    // Validate maxUsage
    if (discount.maxUsage !== null && discount.maxUsage <= 0) {
      newErrors.maxUsage =
        "Max usage must be greater than 0 or leave empty for unlimited";
    }

    // Validate minOrderValue
    if (discount.minOrderValue !== null && discount.minOrderValue <= 0) {
      newErrors.minOrderValue =
        "Minimum order value must be greater than 0 or leave empty for no minimum";
    }

    // Validate maxLimit
    if (discount.maxLimit !== null && discount.maxLimit <= 0) {
      newErrors.maxLimit =
        "Max limit must be greater than 0 or leave empty for no limit";
    }

    // Validate attachedTo values
    if (
      discount.attachedTo.totalBonus !== null &&
      discount.attachedTo.totalBonus <= 0
    ) {
      newErrors.attachedToTotalBonus =
        "Total bonus must be greater than 0 or leave empty";
    }

    if (
      discount.attachedTo.minBonus !== null &&
      discount.attachedTo.minBonus <= 0
    ) {
      newErrors.attachedToMinBonus =
        "Minimum bonus must be greater than 0 or leave empty";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!discount || !validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const updateData: UpdateDiscountData = {
        code: discount.code,
        discountType: discount.discountType,
        value: discount.value === "" ? 0 : Number(discount.value),
        maxUsage: discount.maxUsage,
        maxLimit: discount.maxLimit,
        expiresAt: discount.expiresAt,
        firstTimeUserOnly: discount.firstTimeUserOnly,
        minOrderValue: discount.minOrderValue,
        attachedTo: {
          ...discount.attachedTo,
          value: discount.attachedTo.value === "" ? 0 : Number(discount.attachedTo.value),
        },
        isActive: discount.isActive,
        isPublic: discount.isPublic,
        allowMultipleUse: discount.allowMultipleUse,
        forInstant: discount.forInstant,
        termsAndConditions: discount.termsAndConditions,
      };

      const response = await discountApi.updateDiscount(
        discount._id,
        updateData,
      );
      navigate(`/discounts/${response.discount._id}`);
    } catch (error: any) {
      console.error("Error updating discount:", error);
      setErrors({
        submit:
          error.response?.data?.message ||
          "An error occurred while updating the discount",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    if (!discount) return;

    setDiscount((prev) =>
      prev
        ? {
            ...prev,
            [field]: value,
          }
        : null,
    );

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleAttachedToChange = (field: string, value: any) => {
    if (!discount) return;

    setDiscount((prev) =>
      prev
        ? {
            ...prev,
            attachedTo: {
              ...prev.attachedTo,
              [field]: value,
            },
          }
        : null,
    );

    if (errors[`attachedTo${field.charAt(0).toUpperCase() + field.slice(1)}`]) {
      setErrors((prev) => ({
        ...prev,
        [`attachedTo${field.charAt(0).toUpperCase() + field.slice(1)}`]: "",
      }));
    }
  };

  const handleTermChange = (index: number, value: string) => {
    if (!discount) return;
    const newTerms = [...(discount.termsAndConditions || [])];
    newTerms[index] = value;
    setDiscount((prev) =>
      prev
        ? {
            ...prev,
            termsAndConditions: newTerms,
          }
        : null,
    );
  };

  const addTerm = () => {
    if (!discount) return;
    setDiscount((prev) =>
      prev
        ? {
            ...prev,
            termsAndConditions: [...(prev.termsAndConditions || []), ""],
          }
        : null,
    );
  };

  const removeTerm = (index: number) => {
    if (!discount) return;
    const newTerms = [...(discount.termsAndConditions || [])];
    newTerms.splice(index, 1);
    setDiscount((prev) =>
      prev
        ? {
            ...prev,
            termsAndConditions: newTerms,
          }
        : null,
    );
  };

  const formatDateForInput = (dateString: string | null) => {
    if (!dateString) return "";
    return new Date(dateString).toISOString().slice(0, 16);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (!discount) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500">Discount not found</div>
        <Link
          to="/discounts"
          className="mt-4 inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Discounts
        </Link>
      </div>
    );
  }

  return (
    <div className="flex">
      <Sidebar drawerWidth={250} />
      <div className="flex-1">
        <Header drawerWidth={250} />
        <div className="p-6 mt-20 m-200">
          {/* Header */}
          <div className="flex items-center space-x-4 mb-6">
            <Link
              to={`/discounts/${discount._id}`}
              className="inline-flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              Back to Details
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Edit Discount
              </h1>
              <p className="text-gray-600">Update discount information</p>
            </div>
          </div>

          {/* Form */}
          <div className="max-w-4xl">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Basic Information */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Basic Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Discount Code *
                    </label>
                    <input
                      type="text"
                      value={discount.code}
                      onChange={(e) =>
                        handleInputChange("code", e.target.value.toUpperCase())
                      }
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        errors.code ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="e.g., SAVE10"
                    />
                    {errors.code && (
                      <p className="text-red-500 text-sm mt-1">{errors.code}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Discount Type *
                    </label>
                    <select
                      value={discount.discountType}
                      onChange={(e) =>
                        handleInputChange("discountType", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      <option value="flat">Flat Amount (₹)</option>
                      <option value="percent">Percentage (%)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Value *
                    </label>
                    <input
                      type="number"
                      value={discount.value}
                      onChange={(e) =>
                        handleInputChange(
                          "value",
                          e.target.value === "" ? "" : parseFloat(e.target.value),
                        )
                      }
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        errors.value ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder={
                        discount.discountType === "flat" ? "10" : "5"
                      }
                      min="0"
                      step="0.01"
                    />
                    {errors.value && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.value}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Minimum Order Value
                    </label>
                    <input
                      type="number"
                      value={discount.minOrderValue || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "minOrderValue",
                          e.target.value ? parseFloat(e.target.value) : null,
                        )
                      }
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        errors.minOrderValue
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      placeholder="Leave empty for no minimum"
                      min="0"
                      step="0.01"
                    />
                    {errors.minOrderValue && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.minOrderValue}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Discount Limit (₹)
                    </label>
                    <input
                      type="number"
                      value={discount.maxLimit || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "maxLimit",
                          e.target.value ? parseFloat(e.target.value) : null,
                        )
                      }
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        errors.maxLimit ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="Leave empty for no limit"
                      min="0"
                      step="0.01"
                    />
                    {errors.maxLimit && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.maxLimit}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Usage Settings */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Usage Settings
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Maximum Usage
                    </label>
                    <input
                      type="number"
                      value={discount.maxUsage || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "maxUsage",
                          e.target.value ? parseInt(e.target.value) : null,
                        )
                      }
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        errors.maxUsage ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="Leave empty for unlimited"
                      min="1"
                    />
                    {errors.maxUsage && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.maxUsage}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expiry Date
                    </label>
                    <input
                      type="datetime-local"
                      value={formatDateForInput(discount.expiresAt)}
                      onChange={(e) =>
                        handleInputChange("expiresAt", e.target.value || null)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={discount.firstTimeUserOnly}
                        onChange={(e) =>
                          handleInputChange(
                            "firstTimeUserOnly",
                            e.target.checked,
                          )
                        }
                        className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        First-time plan subscribers only
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Attached Bonus */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Attached Bonus
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bonus Name *
                    </label>
                    <input
                      type="text"
                      value={discount.attachedTo.name}
                      onChange={(e) =>
                        handleAttachedToChange("name", e.target.value)
                      }
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        errors.attachedToName
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      placeholder="e.g., Welcome Bonus"
                    />
                    {errors.attachedToName && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.attachedToName}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bonus Type *
                    </label>
                    <select
                      value={discount.attachedTo.bonusType}
                      onChange={(e) =>
                        handleAttachedToChange("bonusType", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      <option value="flat">Flat Amount (₹)</option>
                      <option value="percent">Percentage (%)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bonus Value *
                    </label>
                    <input
                      type="number"
                      value={discount.attachedTo.value}
                      onChange={(e) =>
                        handleAttachedToChange(
                          "value",
                          e.target.value === "" ? "" : parseFloat(e.target.value),
                        )
                      }
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        errors.attachedToValue
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      placeholder={
                        discount.attachedTo.bonusType === "flat" ? "50" : "10"
                      }
                      min="0"
                      step="0.01"
                    />
                    {errors.attachedToValue && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.attachedToValue}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Total Bonus
                    </label>
                    <input
                      type="number"
                      value={discount.attachedTo.totalBonus || ""}
                      onChange={(e) =>
                        handleAttachedToChange(
                          "totalBonus",
                          e.target.value ? parseFloat(e.target.value) : null,
                        )
                      }
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        errors.attachedToTotalBonus
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      placeholder="Leave empty for no limit"
                      min="0"
                      step="0.01"
                    />
                    {errors.attachedToTotalBonus && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.attachedToTotalBonus}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Minimum Bonus
                    </label>
                    <input
                      type="number"
                      value={discount.attachedTo.minBonus || ""}
                      onChange={(e) =>
                        handleAttachedToChange(
                          "minBonus",
                          e.target.value ? parseFloat(e.target.value) : null,
                        )
                      }
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        errors.attachedToMinBonus
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      placeholder="Leave empty for no minimum"
                      min="0"
                      step="0.01"
                    />
                    {errors.attachedToMinBonus && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.attachedToMinBonus}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Terms and Conditions */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Terms and Conditions
                </h2>
                <div className="space-y-4">
                  {(discount.termsAndConditions || []).map((term, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={term}
                        onChange={(e) =>
                          handleTermChange(index, e.target.value)
                        }
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder={`Term #${index + 1}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeTerm(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remove term"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addTerm}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Add Term
                  </button>
                </div>
              </div>

              {/* Status */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Status
                </h2>
                <div className="space-y-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={discount.isActive}
                      onChange={(e) =>
                        handleInputChange("isActive", e.target.checked)
                      }
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Active (discount will be immediately available for use)
                    </span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={discount.isPublic}
                      onChange={(e) =>
                        handleInputChange("isPublic", e.target.checked)
                      }
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Publicly Available (show in public listings)
                    </span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={discount.allowMultipleUse}
                      onChange={(e) =>
                        handleInputChange("allowMultipleUse", e.target.checked)
                      }
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Allow Multiple Use (User can use this code more than once)
                    </span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={discount.forInstant || false}
                      onChange={(e) =>
                        handleInputChange("forInstant", e.target.checked)
                      }
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Instant Discount (Applicable to Kinnow Instant orders only)
                    </span>
                  </label>
                </div>
              </div>

              {/* Error Message */}
              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <XMarkIcon className="h-5 w-5 text-red-400 mr-2" />
                    <p className="text-red-700">{errors.submit}</p>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex justify-end space-x-4">
                <Link
                  to={`/discounts/${discount._id}`}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </Link>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckIcon className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditDiscount;
